import React from 'react';
import { Button, Empty } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { AutoSizer, Table, Column } from 'react-virtualized';
import { CustomFieldOption } from '../../../../types/custom_fields';
import InputWithPreview from '../../global/InputWithPreview';

type CustomFieldTableProps = {
  value: string;
  headers?: CustomFieldOption[];
  tableDelimiter?: string;
  tableRowDelimiter?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
};

const CustomFieldTable: React.FC<CustomFieldTableProps> = ({
  value,
  headers = [],
  tableDelimiter = '|',
  tableRowDelimiter = '~n~',
  onChange,
  disabled = false,
}) => {
  const { t } = useTranslation('settings');
  const tableRef = React.useRef<Table>(null);

  const parseTableData = React.useCallback(
    (val: string): string[][] => {
      if (!val) return [];

      const rows = val.split(tableRowDelimiter);
      return rows.map(row => {
        if (!row) return [];
        return row.split(tableDelimiter);
      });
    },
    [tableDelimiter, tableRowDelimiter]
  );

  const reconstructValue = React.useCallback(
    (data: string[][]): string => {
      return data
        .filter(row => row.some(cell => cell !== ''))
        .map(row => row.join(tableDelimiter))
        .join(tableRowDelimiter);
    },
    [tableDelimiter, tableRowDelimiter]
  );

  const [tableData, setTableData] = React.useState<string[][]>(() => parseTableData(value));

  React.useEffect(() => {
    setTableData(parseTableData(value));
  }, [value, parseTableData]);

  const sortedHeaders = React.useMemo(() => {
    if (!headers || headers.length === 0) return [];
    return [...headers].sort((a, b) => a.record_number - b.record_number);
  }, [headers]);

  const handleCellChange = React.useCallback(
    (rowIndex: number, colIndex: number, newValue: string) => {
      const newData = [...tableData];

      if (!newData[rowIndex]) {
        newData[rowIndex] = new Array(sortedHeaders.length).fill('');
      }

      while (newData[rowIndex].length < sortedHeaders.length) {
        newData[rowIndex].push('');
      }

      newData[rowIndex][colIndex] = newValue;
      setTableData(newData);
      onChange(reconstructValue(newData));
    },
    [tableData, sortedHeaders.length, onChange, reconstructValue]
  );

  const handleDeleteRow = React.useCallback(
    (rowIndex: number) => {
      const newData = tableData.filter((_, index) => index !== rowIndex);
      setTableData(newData);
      onChange(reconstructValue(newData));
    },
    [tableData, onChange, reconstructValue]
  );

  const handleAddRow = () => {
    const newRow = new Array(sortedHeaders.length).fill('');
    const newData = [...tableData, newRow];
    setTableData(newData);
    onChange(reconstructValue(newData));

    // Scroll to bottom after adding row
    setTimeout(() => {
      if (tableRef.current) {
        tableRef.current.scrollToRow(newData.length - 1);
      }
    }, 50);
  };

  const dataSource = tableData.map((row, index) => {
    const rowData: any = {
      key: `row-${index}`,
      rowIndex: index,
    };

    sortedHeaders.forEach((header, colIndex) => {
      rowData[`col-${colIndex}`] = row[colIndex] || '';
    });

    return rowData;
  });

  const noRowsRenderer = () => {
    return (
      <div className="custom-field-table-empty">
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('customFields.tableNoData')} />
      </div>
    );
  };

  const renderCell = React.useCallback(
    (colIndex: number, cellData: string, rowIndex: number) => {
      const row = dataSource[rowIndex];
      return (
        <InputWithPreview
          value={cellData}
          onChange={e => handleCellChange(row.rowIndex, colIndex, e.target.value)}
          disabled={disabled}
        />
      );
    },
    [dataSource, disabled, handleCellChange]
  );

  const renderActionCell = React.useCallback(
    (rowIndex: number) => {
      const row = dataSource[rowIndex];
      return (
        <div className="custom-field-table-action-cell">
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteRow(row.rowIndex)}
            disabled={disabled}
            size="small"
          />
        </div>
      );
    },
    [dataSource, disabled, handleDeleteRow]
  );

  const columnWidth = 200;
  const actionColumnWidth = 20;

  return (
    <div className="custom-field-table-wrapper">
      <div className="custom-field-table-scroll" style={{ height: 250 }}>
        <AutoSizer>
          {({ height, width }) => (
            <div style={{ height: height - 2, width: width - 2 }}>
              <Table
                ref={tableRef}
                width={width - 2}
                height={height - 2}
                headerHeight={40}
                rowHeight={40}
                rowCount={dataSource.length}
                rowGetter={({ index }) => dataSource[index]}
                noRowsRenderer={noRowsRenderer}
                fixedColumnCount={4}
              >
                {sortedHeaders.map((header, colIndex) => (
                  <Column
                    key={`col-${colIndex}`}
                    label={header.name}
                    dataKey={`col-${colIndex}`}
                    width={columnWidth}
                    minWidth={100}
                    flexGrow={1}
                    cellRenderer={({ cellData, rowIndex }) =>
                      renderCell(colIndex, cellData, rowIndex)
                    }
                    className="custom-field-table-data-column"
                  />
                ))}
                <Column
                  key="action"
                  label=""
                  dataKey="action"
                  width={actionColumnWidth}
                  flexShrink={0}
                  cellRenderer={({ rowIndex }) => renderActionCell(rowIndex)}
                  className="custom-field-table-action-column"
                />
              </Table>
            </div>
          )}
        </AutoSizer>
      </div>
      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onClick={handleAddRow}
        disabled={disabled}
        className="custom-field-table-add-btn"
        block
      >
        {t('customFields.tableAddRow')}
      </Button>
    </div>
  );
};

export default CustomFieldTable;
